<template>
  <main id="main-content">
    <div class="content-page-banner">
      <header class="banner-content">
        <h1 v-if="addRemovePersonReferenceData.addRemovePersonTitleText">
          {{ addRemovePersonReferenceData.addRemovePersonTitleText.informationText }}
        </h1>
        <div class="underline"></div>
        <p v-if="addRemovePersonReferenceData.addRemovePersonDescriptionText" v-html="addRemovePersonReferenceData.addRemovePersonDescriptionText.informationText"></p>
      </header>
    </div>
    <Alert v-if="isExcessChangeRequired && newExcessesList.length === 0" :message="COMPOSITION_NOT_SUPPORTED" :isPlainText="false" alertType="error" />
    <Alert v-else-if="submitPolicyError" :message="ErrorStrings.SUBMIT_POLICY_API_ERROR" alertType="error" />
    <Alert v-else-if="apiStatus.fail" alertType="error">
      <div>
        <p>Sorry, something has gone wrong.</p>
        <p>There may have been a technical error. Please try refreshing the page.</p>
        <p>
          <u>If the change is still not applied</u>
          then the change you are trying to make cannot be applied against that product. Please use change of cover to review products that enable this change.
        </p>
      </div>
    </Alert>
    <div id="multi-step-form">
      <MultiStepFormNavigation :currentPage="currentPage" :links="navLinks" @linkClicked="updateCurrentStep" :disabled="apiStatus.loading || apiStatus.fail" />

      <div id="form-contents">
        <ApiLoading v-if="apiStatus.loading || apiStatus.fail" :apiError="apiStatus.fail">
          <div v-if="isExcessChangeRequired && newExcessesList.length === 0" v-html="COMPOSITION_NOT_SUPPORTED" />
          <div v-else>
            <p>Sorry, something has gone wrong.</p>
            <p>There may have been a technical error. Please try refreshing the page.</p>
            <p>
              <u>If the change is still not applied</u>
              then the change you are trying to make cannot be applied against that product. Please use change of cover to review products that enable this change.
            </p>
          </div>
        </ApiLoading>
        <keep-alive v-else>
          <component
            :is="getComponentName(currentPage)"
            :informationReferenceData="addRemovePersonReferenceData"
            :terminationReasons="terminationReasons"
            :fundNamesNewPerson="fundNamesNewPerson"
            :fundNamesTerminated="fundNamesTerminated"
            @formUpdated="formResponseUpdated"
            :isExcessChangeRequired="isExcessChangeRequired"
            :newExcessesList="newExcessesList"
            @newExcessSelected="newExcessSelected"
            :newComposition="newComposition"
            :newPremium="newPremium"
            :propNewExcess="newExcess"
          />
        </keep-alive>

        <div class="form-page-nav">
          <section class="form-buttons" v-if="!apiStatus.loading && !apiStatus.fail">
            <button id="back-button" v-if="currentPage !== pages.PARTNER.pageNumber" class="secondary" @click="handleBackButton">Back</button>
            <button id="next-button" @click.prevent="handleNextButton" :disabled="isNextButtonDisabled()">
              {{ getNextBtnLabel }}
            </button>
          </section>
        </div>
      </div>
    </div>
  </main>
</template>

<!-- eslint-disable import/no-extraneous-dependencies -->
<script>
import { ChangeCover as Services } from '@/services/MyCover';
import ChangeProductServices from '@/services/MyCover/ChangeCover/ChangeProduct';
import { each, cloneDeep } from 'lodash';
import { ErrorStrings } from '@general/js/constants';
import CommonServices from '@/services/Common';
import { getCurrentDate, removeWhitespaceFromString, DHLError, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { MultiStepFormNavigation, Alert, ApiLoading } from '@/components';
import { OperationType, ContactNumberType, VeteranCardTypeOptions } from '@/enums';
import { mapVeteranCardConditions, convertVeteranCardTypeToCode, mapConcessionCardPayload } from '@/utils/ChangeCoverUtils';
import { formatFormDate } from '@general/js/utils/dateTimeUtils';
import { mapAddress } from '@general/js/utils/JoinNow';
import { LifetimeHealthCoverSituation, ProductType, RelationshipValue } from '@general/js/enums';
import { convertCodeToValue } from '@/utils/CRMUtils';
import { ADD_REMOVE_PERSON_SUCCESS } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import AddRemovePersonMixin from './AddRemovePersonMixins';
import Partner from './Partner.vue';
import Children from './Children.vue';
import Confirmation from './Confirmation.vue';

export default {
  name: 'AddRemovePersonHome',
  mixins: [AddRemovePersonMixin],
  components: {
    MultiStepFormNavigation,
    Alert,
    ApiLoading,
    Partner,
    Children,
    Confirmation,
  },

  data() {
    return {
      currentPage: 1,
      pages: {
        PARTNER: {
          pageNumber: 1,
          pageTitle: 'Partner',
          isInvalid: true,
          componentName: 'Partner',
        },
        CHILDREN: {
          pageNumber: 2,
          pageTitle: 'Children',
          isInvalid: true,
          componentName: 'Children',
        },
        CONFIRMATION: {
          pageNumber: 3,
          pageTitle: 'Confirmation',
          isInvalid: true,
          componentName: 'Confirmation',
        },
      },
      apiStatus: {
        loading: false,
        fail: false,
      },
      submitPolicyError: false,
      terminationReasons: {},
      fundNames: [],
      addRemovePersonReferenceData: {
        partnerFormFieldHelperText: '', // this is to ensure partner component doesn't throw errors in console before ref data is fetched
      },
      isExcessChangeRequired: false,
      newExcessesList: [],
      newExcess: null,
      newComposition: '',
      newPremium: 0,
      ErrorStrings,
    };
  },

  props: {
    coverDetails: {
      type: Object,
    },
  },

  created() {
    // Reset Vuex store when this flow is initialized
    this.resetAddRemoveVuexStore();
  },

  beforeMount() {
    this.fetchReferenceData();
  },

  watch: {
    newExcess() {
      // Need to fetch new premium every time new excess is changed in dropdown
      this.fetchNewPremiumFromEAI();
    },
  },

  computed: {
    navLinks() {
      const links = [
        { linkTitle: this.pages.PARTNER.pageTitle, pages: [this.pages.PARTNER.pageNumber], disabled: false },
        { linkTitle: this.pages.CHILDREN.pageTitle, pages: [this.pages.CHILDREN.pageNumber], disabled: this.pages.PARTNER.isInvalid },
        {
          linkTitle: this.pages.CONFIRMATION.pageTitle,
          pages: [this.pages.CONFIRMATION.pageNumber],
          disabled: this.pages.PARTNER.isInvalid || this.pages.CHILDREN.isInvalid || this.noPolicyChanges,
        },
      ];
      return links;
    },

    getNextBtnLabel() {
      // last page
      if (this.currentPage === this.pages.CONFIRMATION.pageNumber) return 'Submit';
      // other pages
      return 'Next';
    },

    isPartnerChanged() {
      if (this.oldCompositionHasPartner && this.partnerData.existing.keepAdult === false) {
        // partner exists but being terminated
        return true;
      }
      if (!this.oldCompositionHasPartner && this.partnerData.newPartner?.add) {
        // no current partner and adding a new partner
        return true;
      }

      return false;
    },
    areChildrenChanged() {
      if (this.childrenData) {
        if (this.childrenData.terminations.length > 0) {
          // Existing child/children are being terminated
          return true;
        }

        if (this.childrenData?.newChildren.length > 0) {
          // new children are being added
          return true;
        }
      }

      return false;
    },

    noPolicyChanges() {
      return !this.isPartnerChanged && !this.areChildrenChanged;
    },

    // Fund names property when adding a new member in the policy, either partner/child (transferring from another fund)
    // `hiccode`-`fundName` (e.g. GMH-GMHBA Health Fund) is used for the value in the dropdown because of duplicates in `hiccode`, caused problems in displaying the right name
    // Only the `hiccode` will be included in the submit change cover payload
    fundNamesNewPerson() {
      return this.fundNames.map((fund) => {
        return {
          name: fund.name,
          display: fund.name,
          value: `${fund.dhl_hiccode}-${fund.name}`,
          returnValue: `${fund.dhl_hiccode}-${fund.name}`,
          isAutomaticTransfer: fund.dhl_istransfergatewayenabled,
        };
      });
    },

    // Fund names property when terminating a member in the policy, either partner/child
    // `code` is used for the value in the dropdown, to be sent in the submit change cover payload
    fundNamesTerminated() {
      return this.fundNames.map((fund) => {
        return {
          name: fund.name,
          display: fund.name,
          value: fund.dhl_code,
          returnValue: fund.dhl_code,
          isAutomaticTransfer: fund.dhl_istransfergatewayenabled,
        };
      });
    },
  },

  methods: {
    isNextButtonDisabled() {
      switch (this.currentPage) {
        case this.pages.PARTNER.pageNumber:
          return this.pages.PARTNER.isInvalid;
        case this.pages.CHILDREN.pageNumber:
          return this.noPolicyChanges || this.pages.CHILDREN.isInvalid; // return true = disable submit, false = enable submit
        case this.pages.CONFIRMATION.pageNumber:
          return this.noPolicyChanges;
        default:
          return false;
      }
    },

    formResponseUpdated(emittedData) {
      switch (this.currentPage) {
        case this.pages.PARTNER.pageNumber:
          this.pages.PARTNER.isInvalid = emittedData.isInvalid;
          this.updatePartnerData(emittedData.response);
          break;
        case this.pages.CHILDREN.pageNumber:
          this.pages.CHILDREN.isInvalid = emittedData.isInvalid;
          this.updateChildrenData(emittedData.response);
          break;
        default:
          break;
      }
    },

    handleNextButton() {
      if (this.currentPage === this.pages.PARTNER.pageNumber) {
        this.updateCurrentStep(this.pages.CHILDREN.pageNumber);
      } else if (this.currentPage === this.pages.CHILDREN.pageNumber) {
        this.updateCurrentStep(this.pages.CONFIRMATION.pageNumber);
      } else if (this.currentPage === this.pages.CONFIRMATION.pageNumber) {
        this.submitChangesApi(this.craftWholePayload());
      }
    },

    handleBackButton() {
      if (this.currentPage === this.pages.CHILDREN.pageNumber) {
        this.updateCurrentStep(this.pages.PARTNER.pageNumber);
      } else if (this.currentPage === this.pages.CONFIRMATION.pageNumber) {
        this.updateCurrentStep(this.pages.CHILDREN.pageNumber);
      }
    },

    updateCurrentStep(page) {
      this.currentPage = page;
      // need to fetch new composition from EAI when user reaches confirmation page
      if (page === this.pages.CONFIRMATION.pageNumber) {
        this.fetchNewCompositionFromEAI();
      }
    },

    newExcessSelected(newExcess) {
      this.newExcess = newExcess;
    },

    async fetchNewCompositionFromEAI() {
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;

      try {
        const addDependent = this.childrenData.newChildren.some((child) => {
          return child.isStudent !== false; // Children below student age don't have "are you a student" question displayed, isStudent is `null` in such scenarios.
        });

        const addNonStudentDependent = this.childrenData.newChildren.some((child) => {
          return child.isStudent === false;
        });

        const addPartner = !!this.partnerData.newPartner?.add; // !! to make it a boolean value

        const payload = {
          currentDate: getCurrentDate(),
          terminations: {},
          addDependent,
          addNonStudentDependent,
          addPartner,
        };

        // Craft payload.terminations.children
        this.craftChildrenPayload(payload);
        // Delete children obj from payload as we need only terminations
        delete payload.children;
        // Craft payload.terminations.partner
        this.craftPartnerPayload(payload);
        // Delete partner obj from payload as we need only terminations
        delete payload.partner;
        // Craft payload.productList
        this.craftProductListPayload(payload);

        const response = await Services.retrieveNewPolicyScaleFromEAI(this.memberNumber, payload);
        const newScale = response.newScale;
        this.newComposition = newScale;

        const oldScaleResponse = await Services.retrieveNewPolicyScaleFromEAI(this.memberNumber, {
          currentDate: getCurrentDate(),
          memberNumber: this.memberNumber,
          terminations: {},
          addDependent: false,
          addNonStudentDependent: false,
          addPartner: false,
        });
        const oldScale = oldScaleResponse.newScale;
        this.updateCurrentComposition(oldScale);

        if (newScale !== this.currentComposition && !this.doesCurrentExcessAllowComposition(newScale)) {
          this.isExcessChangeRequired = true;
          this.newExcessesList = this.getValidExcessesForComposition(newScale);
          // in excess change scenario, `newExcess` value is emitted by the dropdown in confirmation step, which in turn triggers the watcher to run fetchNewPremiumFromEAI()

          // Need to throw an error if new composition isn't supported by any excesses for current product
          if (this.newExcessesList.length === 0) {
            throw new DHLError(`Error in fetchNewCompositionFromEAI(): New composition (${newScale}) isn't supported by any excesses for current product(s)`);
          }
        } else {
          // in no excess change scenario, fetch new premium from EAI for currently proposed changes
          await this.fetchNewPremiumFromEAI(); // need to await so that loading screen isn't gone before data is fetched
        }
      } catch (error) {
        this.apiStatus.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.loading = false;
      }
    },

    async fetchNewPremiumFromEAI() {
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;

      try {
        const payload = this.craftWholePayload();

        const response = await Services.retrieveNewPolicyPremiumFromEAI(this.memberNumber, payload);
        this.newPremium = response.calculationResultSetTotalResult;
      } catch (error) {
        this.apiStatus.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.loading = false;
      }
    },

    getComponentName(page) {
      switch (page) {
        case this.pages.PARTNER.pageNumber:
          return this.pages.PARTNER.componentName;
        case this.pages.CHILDREN.pageNumber:
          return this.pages.CHILDREN.componentName;
        case this.pages.CONFIRMATION.pageNumber:
          return this.pages.CONFIRMATION.componentName;
        default:
          return '';
      }
    },

    async fetchReferenceData() {
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;

      try {
        const referenceData = await Promise.all([
          Services.getCaptureAdditionalInformationReferenceData(),
          Services.getTerminationReasonFlexCodes(getCurrentDate()),
          Services.getFundNames(),
          // Temp workaround to check if ph/partner is contributor on landing page
          this.coverDetails ? Promise.resolve({ ...this.coverDetails }) : Services.getMpCoverDetails(this.memberNumber),
          Services.getAddRemovePersonReferenceData(),
          Services.getPremiumImpactReferenceData(),
          ChangeProductServices.getChangeProductReferenceData(),
        ]);

        if (referenceData) {
          const wholeRefDataObj = { ...referenceData[4], ...referenceData[0], premiumImpactRefData: { ...referenceData[5] }, ...referenceData[6] };
          this.addRemovePersonReferenceData = wholeRefDataObj;
          this.updateReferenceData(wholeRefDataObj);

          const terminationReasons = referenceData[1].terminationReasons;
          // Transform object to add 'name' & 'value' keys for dropdowns
          this.terminationReasons = each(terminationReasons, (reasons) => {
            reasons.forEach((r) => {
              r.name = r.description;
              r.value = r.code;
            });
          });

          this.fundNames = referenceData[2];

          this.updateMembershipPolicies(referenceData[3].products);
          this.updateMembershipDetails(referenceData[3].membership);
          this.updateAllMembersDetails(referenceData[3].members);
          this.updateEligibilityDetails(referenceData[3].eligibility);
          this.updateCoverComposition();
        }
      } catch (error) {
        this.apiStatus.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.loading = false;
      }
    },

    // function to create the whole submit payload and return it as an object (for reusability)
    craftWholePayload() {
      const payload = {
        memberNumber: this.memberNumber,
        currentDate: getCurrentDate(),
        changeStartDate: getCurrentDate(),
        state: {
          op: OperationType.NONE,
        },
        postcode: {
          op: OperationType.NONE,
        },
        terminations: {
          products: [],
          partner: null,
          children: null,
        },
        productList: [],
        partner: null,
        eligibility: {
          op: OperationType.NONE,
        },
        rebate: {
          op: OperationType.NONE,
        },
      };
      this.craftResidentialAddressPayload(payload);
      this.craftPartnerPayload(payload);
      this.craftChildrenPayload(payload);
      this.craftProductListPayload(payload);
      this.craftMainPolicyHolderPayload(payload);
      this.craftNewExcessPayload(payload);
      this.craftCompositionPayload(payload);

      return payload;
    },

    craftPartnerPayload(payload) {
      const partnerStepData = this.partnerData;
      const existingPartner = partnerStepData.existing;
      const newPartner = partnerStepData.newPartner;

      // If partner exists
      if (this.oldCompositionHasPartner) {
        // Keep on policy = No(false), terminate partner
        if (!existingPartner.keepAdult) {
          payload.terminations.partner = {
            personCode: existingPartner.personCode,
            terminationReason: existingPartner.terminationReason,
            // fundName: existingPartner.fundName ?? null,
          };
          if (existingPartner.fundName) {
            payload.terminations.partner.fundName = existingPartner.fundName;
          }
        }
      }

      // if adding a new partner
      if (newPartner.add) {
        const mobileObj = newPartner.preferredContactMethods.filter((contact) => {
          return contact.type.toLowerCase() === ContactNumberType.MOBILE;
        });

        // if no postal address is added for new partner, send residential address, otherwise, send the postal address with operation ADD
        let postalAddress;
        if (newPartner?.differentPostalAddress) {
          postalAddress = mapAddress(newPartner?.postalAddress, newPartner.differentPostalAddress, newPartner?.POBox);
          postalAddress.op = OperationType.ADD;
        } else {
          postalAddress = { ...payload.residentialAddress };
          postalAddress.op = OperationType.ADD;
        }

        const previousFunds = newPartner.transferFromOthers
          ? newPartner.selectedInsuranceFund.map((fund) => {
              return {
                isAutomaticTransfer: this.isFundAutomaticTransfer(fund.name),
                fundCode: fund.name.split('-')[0], // Split to only get the inbound transfer code and not include fund name
                memberNumber: fund.number,
              };
            })
          : [];

        const veteranCard = newPartner.veteranCard.haveVeteranCard
          ? {
              hasVeteranCard: newPartner.veteranCard.haveVeteranCard,
              veteranCardList: [
                {
                  op: OperationType.ADD,
                  // startDate: newPartner.veteranCard,
                  cardType: convertVeteranCardTypeToCode(newPartner.veteranCard.veteranCardType),
                  acceptedConditions:
                    newPartner.veteranCard.veteranCardType?.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD
                      ? mapVeteranCardConditions(newPartner.veteranCard.veteranCardConditionOptions)
                      : [],
                },
              ],
            }
          : null;

        const concessionCard = newPartner.haveConcessionCard
          ? {
              hasConcessionCard: newPartner.haveConcessionCard,
              cardList: mapConcessionCardPayload(false, [], true, newPartner.selectedConcessionCardOptions),
            }
          : null;

        payload.partner = {
          op: OperationType.ADD,
          personCode: '',
          firstName: newPartner.firstName,
          middleName: newPartner.middleName,
          lastName: newPartner.lastName,
          gender: newPartner.gender,
          dateOfBirth: formatFormDate(newPartner.DOB),
          mobile: mobileObj.length > 0 ? removeWhitespaceFromString(mobileObj[0].number) : '',
          email: newPartner.email,
          isSameTransferFund: Boolean(newPartner.transferFromOthers),
          postalAddress,
          previousFunds,
          veteranCard,
          concessionCard,
        };

        // Add CDA details
        if (this.eligibleForABD) {
          payload.partner.cda = newPartner.abd?.cda;
        }

        // Add LHC only if reason is not NONE
        if (newPartner.lhc && newPartner.lhc.overruledReason?.toLowerCase() !== LifetimeHealthCoverSituation.NONE.toLowerCase()) {
          const lhcDetails = cloneDeep(newPartner.lhc);
          const lhcObj = {
            cae: lhcDetails.cae,
            overruledReason: lhcDetails.overruledReason,
          };
          if (lhcDetails.date) {
            lhcObj.date = formatFormDate(lhcDetails.date);
          }
          if (lhcDetails.dischargeDate) {
            lhcObj.dischargeDate = formatFormDate(lhcDetails.dischargeDate);
          }

          payload.partner.lifetimeHealthCoverOverrule = lhcObj;
        }
      }

      return payload;
    },

    craftChildrenPayload(payload) {
      const allChildren = cloneDeep(this.childrenData);

      // Existing children being terminated
      if (allChildren?.terminations?.length > 0) {
        const childrenTerminations = allChildren.terminations.map((c) => {
          const retObj = {
            personCode: c.personCode,
            terminationReason: c.terminationReason,
          };
          if (c.fundName) {
            retObj.fundName = c.fundName;
          }
          return retObj;
        });

        payload.terminations.children = childrenTerminations;
      }

      // New children being added to the policy
      if (allChildren?.payloads?.length > 0) {
        const newChildrenPayloads = allChildren.payloads.map((child) => {
          let tempChildPayload = {};

          // If no changes to child and keep child on policy
          if (child.op === OperationType.NONE) {
            tempChildPayload = {
              op: OperationType.NONE,
              personCode: child.personCode,
            };
          } else if (child.op === OperationType.ADD || child.op === OperationType.REPLACE) {
            tempChildPayload = cloneDeep(child);
            tempChildPayload.age = this.calculateAgeForChildren(child.dateOfBirth);
          }

          return tempChildPayload;
        });

        payload.children = newChildrenPayloads;
      }
    },

    craftProductListPayload(payload) {
      let productList = [];

      // Logic needs to be confirmed
      const currentExcess = convertCodeToValue(this.excessOptions, this.membershipDetails.excess);

      const findProductExcessType = (policy) => {
        if (policy.productExcessType) {
          return convertCodeToValue(this.productExcessTypeOptions, policy.productExcessType);
        }
        return null;
      };

      productList = this.formattedProductPolicies.map((policy) => {
        const obj = {
          op: OperationType.NONE,
          productCode: policy.productCode,
        };

        // if product type = hospital, add following keys
        if (policy.productType.toLowerCase() === ProductType.HOSPITAL_ONLY.toLowerCase()) {
          obj.excess = currentExcess.toString();
          obj.productExcessType = findProductExcessType(policy);
        }

        return obj;
      });

      payload.productList = productList;
    },

    craftMainPolicyHolderPayload(payload) {
      const policyHolder = this.allMembersData.find((member) => {
        if (member.relationship?.name) {
          return member.relationship.name.toLowerCase() === RelationshipValue.POLICY_HOLDER.toLowerCase();
        }
        return false;
      });

      let postalAddress;
      if (policyHolder.differentPostalAddress) {
        postalAddress = {
          op: OperationType.NONE,
          addressLine1: policyHolder.address.addressLine1 ?? '',
          addressLine2: policyHolder.address.addressLine2 ?? '',
          addressLine3: policyHolder.address.addressLine3 ?? '',
          suburb: policyHolder.address.suburb ?? '',
          postcode: policyHolder.address.postcode ?? '',
          state: policyHolder.address.state ?? '',
          dpId: policyHolder.address.dpId ?? '',
        };
      } else {
        postalAddress = {
          ...payload.residentialAddress,
        };
      }

      const mainPolicyHolder = {
        op: OperationType.NONE,
        personCode: policyHolder.personCode,
        postalAddress,
        veteranCard: null,
        concessionCard: null,
      };

      payload.mainPolicyHolder = mainPolicyHolder;
    },

    craftResidentialAddressPayload(payload) {
      const policyHolder = this.allMembersData.find((member) => {
        if (member.relationship?.name) {
          return member.relationship.name.toLowerCase() === RelationshipValue.POLICY_HOLDER.toLowerCase();
        }
        return false;
      });
      const address = policyHolder.residentialAddress;

      const residentialAddress = {
        op: OperationType.NONE,
        addressLine1: address.addressLine1 ?? '',
        addressLine2: address.addressLine2 ?? '',
        addressLine3: address.addressLine3 ?? '',
        suburb: address.suburb ?? '',
        postcode: address.postcode ?? '',
        state: address.state ?? '',
        dpId: address.dpId ?? '',
      };

      payload.residentialAddress = residentialAddress;
    },

    craftNewExcessPayload(payload) {
      payload.newExcess = {
        op: OperationType.NONE,
      };

      if (this.isExcessChangeRequired) {
        payload.newExcess.op = OperationType.REPLACE;
        payload.newExcess.excess = this.newExcess;
      }

      // add productCode & productExcessType to payload.newExcess
      const hospitalOrPackageProduct = this.formattedProductPolicies.find((policy) => {
        return policy.productType.toLowerCase() === ProductType.HOSPITAL_ONLY.toLowerCase() || policy.productType.toLowerCase() === ProductType.PACKAGE.toLowerCase();
      });

      if (hospitalOrPackageProduct) {
        payload.newExcess.productCode = hospitalOrPackageProduct.productCode;
        payload.newExcess.productExcessType = convertCodeToValue(this.productExcessTypeOptions, hospitalOrPackageProduct.productExcessType);
      }
    },

    craftCompositionPayload(payload) {
      payload.composition = {
        op: OperationType.NONE,
        value: this.newComposition, // EAI requires the composition value returned by the retrieve-new-scale API, even when op:none
      };

      if (this.isExcessChangeRequired) {
        payload.composition.op = OperationType.REPLACE;
      }
    },

    async submitChangesApi(payload) {
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);

      try {
        const response = await Services.submitChangeCover(this.memberNumber, payload);

        if (response.newPayFrequency) {
          // routeParams is for change cover confirmation page
          const productInfo = {
            oldPremium: response.currentPremium,
            newPremium: response.newPremium ?? null, // get the premium from confirmation step call if newPremium is not returned
            frequency: response.newPayFrequency, // frequency is not changed
          };

          setNavigationState({ productInfo });
          this.$router.push({ name: ADD_REMOVE_PERSON_SUCCESS });
        }
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const request = {
              functionName: 'change-cover',
              workItemCategory: 'OTHERS',
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };
            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page
          setNavigationState({ flowName: 'change cover' });
          this.$router.push({ name: ADD_REMOVE_PERSON_SUCCESS });
        } else {
          this.apiStatus.fail = true;
        }
      } finally {
        this.apiStatus.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#multi-step-form {
  margin-top: 16px;
}

.form-page-nav {
  margin-bottom: 30px;
}
</style>
